// src/services/mediaStore.js — decrypted media, kept.
//
// 🟥 THE BROWSER THREW EVERYTHING AWAY ON EVERY RELOAD. There was no on-disk
//    cache of decrypted media anywhere in this client: a refresh re-downloaded
//    every ciphertext object and re-ran secretbox over every byte, three at a
//    time, with one attempt each. That is the whole of "why does the web not
//    save the current session", and it is most of "sometimes they show,
//    sometimes they are broken" — two hundred simultaneous requests against
//    three lanes and no retry is a lottery.
//
// 🟢 THE PHONE HAS NEVER WORKED THIS WAY. It writes the decrypted file into its
//    document directory and links the path onto the message row in SQLite, so
//    the next launch paints the real file on the first frame with no fetch and
//    no decrypt (services/ImagePreloadQueue.ts, MessageDB.patchMessageFields).
//    This is the browser's version of the same idea: IndexedDB holds Blobs
//    natively, so the bytes go in as they are.
//
// 🟢 AND IT IS WHAT MAKES REVOKING SAFE. An object URL is a handle, not the
//    data — so a handle can be dropped when there are too many alive and minted
//    again from the store the moment something asks for it. The old cache could
//    not do that, which is why it revoked URLs out from under live <img> tags
//    and left them broken for the life of the tab.

import { touch, evictions, overBudget, MEDIA_LIVE_URLS, MEDIA_DISK_BUDGET } from './mediaPolicy';
import { ensureFresh } from './plaintextGuard';
import { canSeal, sealBlob, openBlob } from '../crypto/vault';

const DB = 'allo_web_media';
const STORE = 'blobs';
const VERSION = 1;

let _dbp = null;
function idb() {
  if (_dbp) return _dbp;
  _dbp = new Promise((res, rej) => {
    let r;
    try { r = indexedDB.open(DB, VERSION); } catch (e) { rej(e); return; }
    r.onupgradeneeded = () => {
      const d = r.result;
      if (!d.objectStoreNames.contains(STORE)) {
        const s = d.createObjectStore(STORE, { keyPath: 'key' });
        /* 🟢 INDEXED BY LAST USE, so trimming to the budget is a cursor walk
           rather than reading every blob in the store to sort them. */
        s.createIndex('at', 'at');
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  }).catch((e) => { _dbp = null; throw e; });
  return _dbp;
}

const tx = async (mode, fn) => {
  const d = await idb();
  return new Promise((res, rej) => {
    const t = d.transaction(STORE, mode);
    const s = t.objectStore(STORE);
    let out;
    try { out = fn(s); } catch (e) { rej(e); return; }
    t.oncomplete = () => res(out && out.result !== undefined ? out.result : out);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  });
};

/* ─── the volatile half: object URLs ──────────────────────────────────────── */
const _live = new Map();   // key → objectURL
let _order = [];           // true LRU, touched on every hit

function mint(key, blob) {
  const url = URL.createObjectURL(blob);
  _live.set(key, url);
  _order = touch(_order, key);
  /* 🟢 REVOKING IS SAFE NOW, because the bytes are still on disk and anything
     that asks again gets a fresh handle. The rule the old cache broke — never
     revoke what somebody is rendering — is replaced by a rule that cannot be
     broken: whatever is rendering it can ask for it again. */
  for (const dead of evictions(_order, MEDIA_LIVE_URLS)) {
    const u = _live.get(dead);
    if (u && dead !== key) { try { URL.revokeObjectURL(u); } catch { /* already gone */ } }
    _live.delete(dead);
  }
  _order = _order.filter((k) => _live.has(k));
  return url;
}

/** A handle we already have, or null. Touches the LRU — a hit IS a use. */
export function liveUrl(key) {
  if (!key || !_live.has(key)) return null;
  _order = touch(_order, key);
  return _live.get(key);
}

/** Mint a handle from the stored bytes. null when this key was never stored. */
export async function mintFromStore(key) {
  /* 🟢 the same guard the history reader awaits — the decrypted bytes on this
     disk are the same kind of thing as the decrypted words, and one of them
     surviving a wipe the other did not is how a cache becomes the leak. */
  await ensureFresh();
  if (!key) return null;
  try {
    const row = await tx('readonly', (s) => s.get(key));
    if (!row?.blob && !row?.sealed) return null;
    /* the read is the use — keep the budget honest without a second write path */
    tx('readwrite', (s) => s.put({ ...row, at: Date.now() })).catch(() => {});
    /* 🟢 a row written before this browser enrolled is still a plain Blob; one
       written after is sealed. The row says which, so neither has to be guessed
       and a half-migrated store simply works. */
    const blob = row.sealed ? await openBlob(row.sealed) : row.blob;
    return mint(key, blob);
  } catch (e) {
    /* 🟥 A LOCKED VAULT IS NOT A MISSING FILE. Collapsed into null, it becomes
       "this image is gone", and the layer above answers by downloading and
       decrypting it all over again — every image, every render, on a browser
       that is holding all of them and is merely locked. Let it out. */
    if (e?.name === 'VaultLocked') throw e;
    return null;
  }
}

/** Store the decrypted bytes and hand back a handle. */
export async function keep(key, blob) {
  const url = mint(key, blob);
  try {
    /* 🟥 `canSeal`, NOT `isEnrolled` — the question is whether the key is in
       hand right now. Sealing with a null key is how a cache fills with records
       nothing can ever open. */
    const row = canSeal()
      ? { key, sealed: await sealBlob(blob), bytes: blob?.size || 0, at: Date.now() }
      : { key, blob, bytes: blob?.size || 0, at: Date.now() };
    await tx('readwrite', (s) => s.put(row));
    trim();
  } catch { /* a store that will not write is a slower session, not a broken one */ }
  return url;
}

/**
 * Rewrite every sealed row back as plain bytes. Used when the code is removed.
 *
 * 🟢 SAFE TO FAIL PARTWAY, AND THAT IS BY CONSTRUCTION. Each row already says
 *    whether it is sealed, and the reader handles both shapes — so a migration
 *    that stops halfway leaves a mixture that simply works, rather than a store
 *    half of which is unreadable. That property is why removing a code can be
 *    offered at all: the alternative was wiping the cache, which would have made
 *    "I don't want a code any more" cost the user every photo they had loaded.
 *
 * 🟥 REQUIRES THE KEY IN HAND. Called locked, it would walk the whole store
 *    throwing on every row and leave everything sealed with no vault to open it.
 */
export async function unsealAll() {
  if (!canSeal()) throw new Error('locked');
  const rows = await tx('readonly', (st) => st.getAll());
  let done = 0;
  for (const row of (rows || [])) {
    if (!row?.sealed) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      const blob = await openBlob(row.sealed);
      const next = { key: row.key, blob, bytes: row.bytes || blob.size || 0, at: row.at || Date.now() };
      // eslint-disable-next-line no-await-in-loop
      await tx('readwrite', (st) => st.put(next));
      done += 1;
    } catch { /* one unreadable row must not stop the rest being freed */ }
  }
  return done;
}

/** Forget one key entirely — the bytes were wrong, or the object is gone. */
export async function forget(key) {
  const u = _live.get(key);
  if (u) { try { URL.revokeObjectURL(u); } catch { /* already gone */ } }
  _live.delete(key);
  _order = _order.filter((k) => k !== key);
  try { await tx('readwrite', (s) => s.delete(key)); } catch { /* nothing to do */ }
}

/* 🟢 TRIMMED IN THE BACKGROUND, AND ONLY WHEN IT MATTERS. Reading every row's
   size on every write would make the store the slowest part of opening a chat;
   this runs after a write lands and skips out the moment it is under budget. */
let _trimming = false;
async function trim() {
  if (_trimming) return;
  _trimming = true;
  try {
    const rows = await tx('readonly', (s) => new Promise((res) => {
      const out = [];
      const c = s.index('at').openCursor();
      c.onsuccess = () => {
        const cur = c.result;
        if (!cur) { res(out); return; }
        /* 🟥 THE VALUE, NOT THE BLOB. `cursor.value` on a store of Blobs would
           pull every byte in the database into memory to decide what to delete
           — the exact opposite of what a trim is for. `bytes` and `at` are
           written beside the blob so the walk stays cheap. */
        out.push({ key: cur.value.key, bytes: cur.value.bytes, at: cur.value.at });
        cur.continue();
      };
      c.onerror = () => res(out);
    }));
    const drop = overBudget(rows, MEDIA_DISK_BUDGET);
    if (drop.length) await tx('readwrite', (s) => { drop.forEach((k) => s.delete(k)); });
  } catch { /* the budget is a courtesy, not a correctness property */ } finally {
    _trimming = false;
  }
}

/** Every handle goes on sign-out — the next account must not inherit them. */
export async function clearAll() {
  for (const u of _live.values()) { try { URL.revokeObjectURL(u); } catch { /* gone */ } }
  _live.clear();
  _order = [];
  try { await tx('readwrite', (s) => s.clear()); } catch { /* nothing to do */ }
}
